import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Anchor,
  LogOut,
  RefreshCw,
  CheckCircle,
  XCircle,
  Trash2,
  Mail,
  Phone,
  Users,
  Calendar,
  DollarSign,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "am_admin_token";

function statusColor(status) {
  switch (status) {
    case "confirmed":
      return "bg-[color:var(--am-pink)] text-navy";
    case "cancelled":
      return "bg-gray-200 text-gray-700 line-through";
    case "paid":
      return "bg-navy text-white";
    default:
      return "bg-white border border-[color:var(--am-line)] text-navy";
  }
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [spotlights, setSpotlights] = useState([]);
  const [spotForm, setSpotForm] = useState({ name: "", tier: "", quote: "", photo_url: "" });
  const [loggingIn, setLoggingIn] = useState(false);

  async function login() {
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      toast.success("Welcome, Captain.");
    } catch (e) {
      toast.error("Invalid password.");
    } finally {
      setLoggingIn(false);
    }
  }

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setBookings([]);
  }

  async function load() {
    if (!token) return;
    setLoading(true);
    try {
      const [bRes, iRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/membership-inquiries`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/spotlights`),
      ]);
      setBookings(bRes.data);
      setInquiries(iRes.data || []);
      setSpotlights(sRes.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Failed to load dashboard.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function approveMember(id) {
    if (!window.confirm("Approve this applicant as an active Captain's Club member? They will receive a welcome email with their lounge access code.")) return;
    try {
      await axios.post(
        `${API}/admin/membership-inquiries/${id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Member approved — welcome email sent.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed.");
    }
  }

  async function addSpotlight() {
    if (!spotForm.name || !spotForm.quote) {
      toast.error("Name and quote are required.");
      return;
    }
    try {
      await axios.post(`${API}/admin/spotlights`, spotForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Spotlight added — live on the homepage.");
      setSpotForm({ name: "", tier: "", quote: "", photo_url: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add spotlight.");
    }
  }

  async function removeSpotlight(id) {
    if (!window.confirm("Remove this spotlight from the homepage?")) return;
    try {
      await axios.delete(`${API}/admin/spotlights/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Spotlight removed.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  }

  async function updateStatus(id, status) {
    try {
      await axios.patch(
        `${API}/admin/bookings/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Booking ${status}.`);
      load();
    } catch {
      toast.error("Update failed.");
    }
  }

  async function markPaid(id) {
    try {
      await axios.post(
        `${API}/admin/bookings/${id}/mark-paid`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Marked as deposit received.");
      load();
    } catch {
      toast.error("Update failed.");
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Booking deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  }

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-md bg-white border border-[color:var(--am-line)] p-10">
          <div className="flex items-center gap-2 text-navy mb-8">
            <Anchor size={22} strokeWidth={1.5} />
            <span className="font-serif-display text-2xl">Anchor Management</span>
          </div>
          <div className="overline mb-3">Captain's Quarters</div>
          <h1 className="font-serif-display text-4xl text-navy mb-8 leading-tight">Sign in</h1>
          <Label htmlFor="pw" className="overline block mb-2">Password</Label>
          <Input
            id="pw"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            className="rounded-none"
            data-testid="admin-password-input"
          />
          <button
            onClick={login}
            disabled={loggingIn || !password}
            className="btn-primary w-full justify-center mt-6"
            data-testid="admin-login-button"
          >
            {loggingIn ? "Signing in..." : "Sign In"}
          </button>
        </div>
      </div>
    );
  }

  const pendingCount = bookings.filter((b) => b.status === "pending").length;
  const confirmedCount = bookings.filter((b) => b.status === "confirmed").length;
  const revenue = bookings
    .filter((b) => b.status !== "cancelled")
    .reduce((sum, b) => sum + (b.estimated_total || 0), 0);

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard">
      <header className="nav-glass sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2 text-navy">
            <Anchor size={22} strokeWidth={1.5} />
            <span className="font-serif-display text-2xl">Admin · Anchor Management</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="btn-secondary py-2 px-4 text-[0.72rem]"
              data-testid="admin-refresh-button"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={logout}
              className="btn-primary py-2 px-4 text-[0.72rem]"
              data-testid="admin-logout-button"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
        <div className="overline mb-5">Overview</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <Stat label="Total Bookings" value={bookings.length} />
          <Stat label="Pending" value={pendingCount} />
          <Stat label="Confirmed" value={confirmedCount} />
          <Stat label="Pipeline" value={`$${revenue.toLocaleString()}`} />
        </div>

        <div className="overline mb-5">All Bookings</div>
        {loading ? (
          <div className="text-[color:var(--am-muted)] py-20 text-center">Loading…</div>
        ) : bookings.length === 0 ? (
          <div className="bg-white border border-[color:var(--am-line)] p-12 text-center text-[color:var(--am-muted)]">
            No bookings yet. Customers' requests will appear here.
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((b) => (
              <article
                key={b.id}
                className="bg-white border border-[color:var(--am-line)] p-6 grid lg:grid-cols-12 gap-6"
                data-testid={`admin-booking-${b.id}`}
              >
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`text-[0.62rem] tracking-[0.22em] uppercase px-2 py-1 ${statusColor(b.status)}`}>
                      {b.status}
                    </span>
                    {b.payment_status === "deposit_paid" && (
                      <span className="text-[0.62rem] tracking-[0.22em] uppercase bg-green-100 text-green-800 px-2 py-1">
                        Deposit Paid
                      </span>
                    )}
                    {b.payment_status === "wire_sent" && (
                      <span className="text-[0.62rem] tracking-[0.22em] uppercase bg-amber-100 text-amber-800 px-2 py-1" data-testid={`wire-sent-badge-${b.id}`}>
                        Wire Sent
                      </span>
                    )}
                    <span className="text-xs text-[color:var(--am-muted)]">
                      {formatDate(b.created_at)}
                    </span>
                  </div>
                  <div className="font-serif-display text-2xl text-navy">{b.full_name}</div>
                  <div className="text-xs tracking-[0.18em] uppercase text-[color:var(--am-muted)] mt-1">
                    {b.experience}
                  </div>
                  <div className="mt-3 space-y-1 text-sm text-[color:var(--am-muted)]">
                    <div className="flex items-center gap-2"><Mail size={13} /> {b.email}</div>
                    <div className="flex items-center gap-2"><Phone size={13} /> {b.phone}</div>
                    <div className="flex items-center gap-2"><Users size={13} /> {b.guests} guests</div>
                  </div>
                </div>

                <div className="lg:col-span-4">
                  <div className="overline mb-2">Dates</div>
                  <ul className="text-sm text-navy space-y-1">
                    {b.dates.map((d, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Calendar size={13} className="text-[color:var(--am-muted)]" />
                        {formatDate(d.date)} · {d.overnight ? "Overnight" : `${d.start_time} · ${d.duration_hours}h`}
                      </li>
                    ))}
                  </ul>
                  {b.add_ons.length > 0 && (
                    <>
                      <div className="overline mt-4 mb-2">Add-ons</div>
                      <div className="flex flex-wrap gap-2">
                        {b.add_ons.map((a) => (
                          <span key={a} className="text-xs bg-pink-soft text-navy px-2 py-1">{a}</span>
                        ))}
                      </div>
                    </>
                  )}
                  {b.notes && (
                    <>
                      <div className="overline mt-4 mb-2">Notes</div>
                      <p className="text-sm text-[color:var(--am-muted)] italic">"{b.notes}"</p>
                    </>
                  )}
                </div>

                <div className="lg:col-span-3 flex flex-col justify-between">
                  <div>
                    <div className="overline mb-2">Total</div>
                    <div className="font-serif-display text-3xl text-navy flex items-center gap-1">
                      <DollarSign size={20} /> {b.estimated_total.toLocaleString()}
                    </div>
                    <div className="text-xs text-[color:var(--am-muted)] mt-1">
                      Deposit: ${b.deposit_amount?.toLocaleString?.() ?? (b.estimated_total / 2).toFixed(0)}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {b.payment_status !== "deposit_paid" && (
                      <button
                        onClick={() => markPaid(b.id)}
                        className="text-[0.62rem] tracking-[0.22em] uppercase border border-green-700 text-green-800 px-3 py-2 hover:bg-green-50 transition-colors"
                        data-testid={`mark-paid-${b.id}`}
                      >
                        <DollarSign size={12} className="inline mr-1" />Wire Received
                      </button>
                    )}
                    {b.status !== "confirmed" && (
                      <button
                        onClick={() => updateStatus(b.id, "confirmed")}
                        className="text-[0.62rem] tracking-[0.22em] uppercase border border-navy text-navy px-3 py-2 hover:bg-navy hover:text-white transition-colors"
                        data-testid={`confirm-${b.id}`}
                      >
                        <CheckCircle size={12} className="inline mr-1" />Confirm
                      </button>
                    )}
                    {b.status !== "cancelled" && (
                      <button
                        onClick={() => updateStatus(b.id, "cancelled")}
                        className="text-[0.62rem] tracking-[0.22em] uppercase border border-[color:var(--am-muted)] text-[color:var(--am-muted)] px-3 py-2 hover:border-navy hover:text-navy transition-colors"
                        data-testid={`cancel-${b.id}`}
                      >
                        <XCircle size={12} className="inline mr-1" />Cancel
                      </button>
                    )}
                    <button
                      onClick={() => remove(b.id)}
                      className="text-[0.62rem] tracking-[0.22em] uppercase border border-red-300 text-red-700 px-3 py-2 hover:bg-red-50 transition-colors"
                      data-testid={`delete-${b.id}`}
                    >
                      <Trash2 size={12} className="inline mr-1" />Delete
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* CAPTAIN'S CLUB INQUIRIES */}
        <div className="overline mt-16 mb-5">Captain's Club Applications</div>
        {inquiries.length === 0 ? (
          <div className="bg-white border border-[color:var(--am-line)] p-8 text-center text-[color:var(--am-muted)]">
            No membership inquiries yet.
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-inquiries-list">
            {inquiries.map((q) => (
              <article
                key={q.id}
                className="bg-white border border-[color:var(--am-line)] p-6 grid lg:grid-cols-12 gap-6"
                data-testid={`admin-inquiry-${q.id}`}
              >
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`text-[0.62rem] tracking-[0.22em] uppercase px-2 py-1 ${
                      q.status === "member" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                    }`}>
                      {q.status}
                    </span>
                    <span className="text-[0.62rem] tracking-[0.22em] uppercase bg-pink-soft text-navy px-2 py-1">
                      {q.tier}
                    </span>
                    <span className="text-xs text-[color:var(--am-muted)]">{formatDate(q.created_at)}</span>
                  </div>
                  <div className="font-serif-display text-2xl text-navy">{q.full_name}</div>
                  <div className="mt-3 space-y-1 text-sm text-[color:var(--am-muted)]">
                    <div className="flex items-center gap-2"><Mail size={13} /> {q.email}</div>
                    <div className="flex items-center gap-2"><Phone size={13} /> {q.phone}</div>
                  </div>
                </div>
                <div className="lg:col-span-4">
                  {q.notes && (
                    <>
                      <div className="overline mb-2">Notes</div>
                      <p className="text-sm text-[color:var(--am-muted)] italic">"{q.notes}"</p>
                    </>
                  )}
                </div>
                <div className="lg:col-span-3 flex flex-col justify-between">
                  <div>
                    <div className="overline mb-2">Annual Fee</div>
                    <div className="font-serif-display text-3xl text-navy flex items-center gap-1">
                      <DollarSign size={20} /> {(q.annual_fee || 0).toLocaleString()}
                    </div>
                  </div>
                  {q.status !== "member" && (
                    <button
                      onClick={() => approveMember(q.id)}
                      className="mt-4 text-[0.62rem] tracking-[0.22em] uppercase border border-green-700 text-green-800 px-3 py-2 hover:bg-green-50 transition-colors"
                      data-testid={`approve-member-${q.id}`}
                    >
                      <CheckCircle size={12} className="inline mr-1" />Approve as Member
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* MEMBER SPOTLIGHTS */}
        <div className="overline mt-16 mb-5">Homepage Member Spotlights</div>
        <div className="bg-white border border-[color:var(--am-line)] p-6 mb-4" data-testid="admin-spotlight-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="overline block mb-2">Name</Label>
              <Input
                value={spotForm.name}
                onChange={(e) => setSpotForm({ ...spotForm, name: e.target.value })}
                placeholder="Sarah W."
                className="rounded-none"
                data-testid="spotlight-name-input"
              />
            </div>
            <div>
              <Label className="overline block mb-2">Tier (optional)</Label>
              <Input
                value={spotForm.tier}
                onChange={(e) => setSpotForm({ ...spotForm, tier: e.target.value })}
                placeholder="Captain"
                className="rounded-none"
                data-testid="spotlight-tier-input"
              />
            </div>
          </div>
          <div className="mt-4">
            <Label className="overline block mb-2">Quote</Label>
            <textarea
              value={spotForm.quote}
              onChange={(e) => setSpotForm({ ...spotForm, quote: e.target.value })}
              placeholder="A short, vivid quote (max 280 chars)"
              rows={3}
              maxLength={280}
              className="w-full p-3 border border-[color:var(--am-line)] rounded-none focus:outline-none focus:border-navy"
              data-testid="spotlight-quote-input"
            />
          </div>
          <div className="mt-4">
            <Label className="overline block mb-2">Photo URL (optional)</Label>
            <Input
              value={spotForm.photo_url}
              onChange={(e) => setSpotForm({ ...spotForm, photo_url: e.target.value })}
              placeholder="https://..."
              className="rounded-none"
              data-testid="spotlight-photo-input"
            />
          </div>
          <button
            onClick={addSpotlight}
            disabled={!spotForm.name || !spotForm.quote}
            className="btn-primary mt-5"
            data-testid="spotlight-add-button"
          >
            Add Spotlight
          </button>
        </div>
        {spotlights.length > 0 && (
          <div className="space-y-3" data-testid="admin-spotlights-list">
            {spotlights.map((s) => (
              <article
                key={s.id}
                className="bg-white border border-[color:var(--am-line)] p-5 flex items-start justify-between gap-4"
                data-testid={`admin-spotlight-${s.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-xl text-navy">
                    {s.name}{s.tier ? ` · ${s.tier}` : ""}
                  </div>
                  <p className="text-sm italic text-[color:var(--am-muted)] mt-2 line-clamp-2">"{s.quote}"</p>
                </div>
                <button
                  onClick={() => removeSpotlight(s.id)}
                  className="text-[0.62rem] tracking-[0.22em] uppercase border border-red-300 text-red-700 px-3 py-2 hover:bg-red-50 transition-colors shrink-0"
                  data-testid={`spotlight-delete-${s.id}`}
                >
                  <Trash2 size={12} className="inline mr-1" />Remove
                </button>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-[color:var(--am-line)] p-6">
      <div className="overline mb-3">{label}</div>
      <div className="font-serif-display text-4xl text-navy">{value}</div>
    </div>
  );
}
